import { inject, Injectable } from '@angular/core';

import { concatMap, filter, map, mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { CategoryApplicationConfigurationService } from '@era/settings-sdk';
import { environment } from '@rocksoft/environment';
import { from } from 'rxjs';
import { compareApplications, safeMap } from '../effects.helpers';
import { getAncestors } from '../router/category';
import {
  ApplicationsApiActions,
  CategoryApplicationsEffectActions,
  CategoryApplicationsResolverActions,
  CategoryApplicationViewActions,
  DeviceConfiguredApplicationsResolverActions,
} from './category-applications.actions';
import { applicationConfigurationFrom } from './category-applications.state';

@Injectable()
export class CategoryApplicationsEffects {
  private readonly actions = inject(Actions);
  private readonly categoryApplicationService = inject(CategoryApplicationConfigurationService);

  protected features = environment.features;

  /**
   * The category application configuration has been updated.
   * Effect: we submit the update to the api and dispatch an error if applicable.
   */
  categoryApplicationLinkUpdatedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryApplicationViewActions.applicationConfigurationsUpdated),
      mergeMap((action) => {
        const categoryType = action.categoryType;

        return safeMap(
          this.categoryApplicationService.updateAllApplicationsForCategory({
            categoryType,
            categoryId: action.id,
            operation: compareApplications(action),
          }),
          () =>
            CategoryApplicationsEffectActions.categoryApplicationsRequested({
              id: action.id,
              categoryType: action.categoryType,
            }),
          (error) =>
            ApplicationsApiActions.categoryApplicationConfigurationsUpdatedError({
              id: action.id,
              categoryType: action.categoryType,
              error,
            })
        );
      })
    );
  });

  /**
   * The resolver has requested applications for a category.
   * Effect: we dispatch actions for the category and all its ancestors.
   */
  categoryApplicationsResolverEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryApplicationsResolverActions.categoryApplicationsRequested),
      concatMap((action) => {
        const ancestors = getAncestors(action.category);
        return from(
          ancestors.map((ancestor) =>
            CategoryApplicationsEffectActions.categoryApplicationsRequested({
              id: ancestor.categoryId,
              categoryType: ancestor.categoryType,
            })
          )
        );
      })
    );
  });

  /**
   * The (re)load of the category applications has been requested.
   * Effect: we load the state from the api.
   */
  categoryApplicationsRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        DeviceConfiguredApplicationsResolverActions.categoryApplicationsRequested,
        CategoryApplicationsEffectActions.categoryApplicationsRequested
      ),
      filter((action) => action.id != null && action.id !== ''),
      mergeMap((action) => {
        const categoryType = action.categoryType;

        return safeMap(
          this.categoryApplicationService.getAllApplicationsForCategory({
            categoryType,
            categoryId: action.id,
          }),
          (categoryApplications) =>
            ApplicationsApiActions.categoryApplicationsLoaded({
              applications: categoryApplications.map(applicationConfigurationFrom),
              categoryType: action.categoryType,
            }),
          (error) =>
            ApplicationsApiActions.categoryApplicationsLoadedError({
              error,
            })
        );
      })
    );
  });

  /**
   * The applications couldn't be updated.
   * Effect: we request a reload of the state from the api.
   */
  categoryApplicationsUpdatedErrorEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ApplicationsApiActions.categoryApplicationConfigurationsUpdatedError),
      map((action) =>
        CategoryApplicationsEffectActions.categoryApplicationsRequested({
          id: action.id,
          categoryType: action.categoryType,
        })
      )
    );
  });
}
